import { Container, Group, Box, Text, Paper, Title, ActionIcon, Tooltip } from '@mantine/core'
import { useRef } from 'react'
import styles from './SchedulerPageShell.module.css'
import { VehicleResponse } from '@/api/main/types.gen'
import { useSchedulerCalculationsStore } from '@/stores/schedulerCalculations'
import { VehicleStats } from './VehicleStats'
import { useScrollbarSync } from './hooks/useScrollbarSync'
import { useNavigate } from '@tanstack/react-router'
import { TbCalendarPlus } from 'react-icons/tb'
import { PermissionGuard } from '@/utils/permissions'
import { useAuthStore } from '@/stores/auth'
import { useTranslation } from 'react-i18next'

const SchedulerPageShell = ({
  children,
  title,
  action,
  contentKey,
  vehicles,
  startDate,
  endDate,
}: {
  children: React.ReactNode
  title: string | React.ReactNode
  action?: React.ReactNode
  contentKey?: string // Key to trigger scrollbar update when content changes
  vehicles: VehicleResponse[]
  startDate: string
  endDate: string
}) => {
  const { t } = useTranslation('scheduling')
  const { userPermissions } = useAuthStore()
  const topScrollRef = useRef<HTMLDivElement>(null)
  const contentScrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const vehicleHeaderScrollRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { calculations } = useSchedulerCalculationsStore()

  useScrollbarSync({
    topScrollRef,
    contentScrollRef,
    contentRef,
    vehicleHeaderScrollRef,
    contentKey,
  })

  const handleAddDrive = (vehicleId: string) => {
    navigate({
      to: '/scheduling/$startDate/$endDate/drive/$action/$id',
      params: {
        startDate: encodeURIComponent(startDate),
        endDate: encodeURIComponent(endDate),
        action: 'new',
        id: vehicleId,
      },
    })
  }

  return (
    <Container
      size="xl"
      styles={{
        root: {
          paddingInline: 0,
          maxWidth: '100%',
        },
      }}
    >
      <Box style={{ position: 'relative', width: '100%' }}>
        <Group justify="space-between" align="center">
          <Title order={1} size="h2">
            {title}
          </Title>
          {action}
        </Group>
        {/* Top scrollbar indicator */}
        <Box ref={topScrollRef} className={styles.topScrollbarContainer}>
          <Box style={{ width: '100%', minHeight: '1px' }} />
        </Box>

        {/* Vehicle header - sticky and scrollable */}
        {vehicles && vehicles.length > 0 && (
          <Paper
            shadow="sm"
            radius="md"
            withBorder
            ref={vehicleHeaderScrollRef}
            className={styles.contentScrollbar}
            style={{
              position: 'sticky',
              top: 'calc(var(--mantine-header-height, 59px) + 10px)',
              zIndex: 10,
              overflowX: 'hidden',
            }}
            mb="md"
          >
            <Box
              id="vehicle-header-content"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${vehicles.length}, minmax(200px, 1fr))`,
                gap: '1rem',
                boxSizing: 'border-box',
              }}
              p="xs"
            >
              {vehicles.map(vehicle => (
                <Box
                  key={vehicle.id}
                  style={{
                    textAlign: 'left',
                    boxSizing: 'border-box',
                    background: 'var(--mantine-color-body)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box style={{ flex: 1 }}>
                    <Text fw={700} size="lg">
                      {vehicle.id.toUpperCase()}
                    </Text>
                    <VehicleStats
                      estimatedTime={calculations[vehicle.id]?.estimatedTime}
                      drivenTime={calculations[vehicle.id]?.drivenTime}
                    />
                  </Box>
                  <PermissionGuard userPermissions={userPermissions} resource="scheduling" action="write">
                    <Tooltip
                      label={t('scheduleDrivesFor', { vehicleId: vehicle.id.toUpperCase() })}
                      withArrow
                      position="top"
                    >
                      <ActionIcon variant="subtle" size="md" onClick={() => handleAddDrive(vehicle.id)}>
                        <TbCalendarPlus />
                      </ActionIcon>
                    </Tooltip>
                  </PermissionGuard>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        <Box ref={contentScrollRef} className={styles.contentScrollbar}>
          <Box ref={contentRef}>{children}</Box>
        </Box>
      </Box>
    </Container>
  )
}

export default SchedulerPageShell
